import {
  FC,
  Dispatch,
  SetStateAction,
  useState,
  useCallback,
  FormEvent,
  useMemo,
} from "react";
import { observer } from "mobx-react-lite";
import { useTranslation, Trans } from "react-i18next";
import { Fade } from "@mui/material";
import { CurrenciesStore, UserStore } from "stores";
import { ModalButton, CloseModalButton, MainButton } from "components/Buttons";
import { ModalWarningInfo } from "components/Modals";
import { CurrencyInput, StyledTextInput, Select } from "components/Inputs";
import WithdrawInfoRows from "./WithdrawInfoRows";
import { AppText, Checkbox, Warning } from "components";
import { toLocaleStringWithCurrency } from "helpers/funcs";
import { SelectItemProps } from "components/Inputs/types";
import { setupMFAVerification } from "api/auth";
import {
  ModalContent,
  ModalHeading,
  InnerContentForm,
  MainContainer,
  TransactionGroup,
  SemiContainer,
} from "../styled";
import type { CurrencyInterface } from "helpers/types";
import type { FntClaimInfo } from "api/types/fntClaim";

import { ReactComponent as LockIcon } from "assets/icons/lock-unlocked.svg";

interface MainWithdrawStepProps {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
  setSkipConfirm: Dispatch<SetStateAction<boolean>>;
  skipConfirm: boolean;
  onClose: () => void;
  paymentType?: CurrencyInterface;
  claimInfo: FntClaimInfo | null;
  claimInfos: FntClaimInfo[] | null;
  currencies: CurrencyInterface[] | null;
  chosenNetwork: string;
  withdrawAddress: string;
  setWithdrawAddress: Dispatch<SetStateAction<string>>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  withdrawMemo: string;
  setWithdrawMemo: Dispatch<SetStateAction<string>>;
  setCurrentClaimInfo: Dispatch<SetStateAction<FntClaimInfo | null>>;
  hasMemo: boolean;
  isLoading: boolean;
}

const MainWithdrawStep: FC<MainWithdrawStepProps> = ({
  onSubmit,
  onClose,
  paymentType,
  skipConfirm,
  setSkipConfirm,
  claimInfo,
  claimInfos,
  setCurrentClaimInfo,
  chosenNetwork,
  withdrawAddress,
  setWithdrawAddress,
  setIsLoading,
  withdrawMemo,
  setWithdrawMemo,
  hasMemo,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { currencies } = CurrenciesStore;
  const { isMFAEnabled } = UserStore;
  const [isWarnConfirmed, setIsWarnConfirmed] = useState<boolean>(false);

  const change2Fa = useCallback(() => {
    setIsLoading(true);
    setupMFAVerification().finally(() => setIsLoading(false));
  }, [setIsLoading]);

  const cabinetOptions = useMemo<Array<SelectItemProps>>(() => {
    if (claimInfos) {
      return claimInfos.map(({ cabinet }) => ({
        value: cabinet,
        label: cabinet,
        key: cabinet,
      }));
    }
    return [];
  }, [claimInfos]);

  const onSelectCabinet = useCallback(
    (selectedCabinet: string) => {
      if (claimInfos) {
        const newChosenClaimInfo = claimInfos.find(
          ({ cabinet }) => selectedCabinet === cabinet
        );
        if (newChosenClaimInfo) setCurrentClaimInfo(newChosenClaimInfo);
      }
    },
    [setCurrentClaimInfo, claimInfos]
  );

  return (
    <Fade in timeout={500}>
      <ModalContent>
        <CloseModalButton onClose={onClose} />
        <ModalHeading>Token {t("CLAIM")}</ModalHeading>
        <InnerContentForm onSubmit={(e) => onSubmit(e)}>
          {chosenNetwork && paymentType?.networkName && (
            <ModalWarningInfo
              text={
                <Trans
                  i18nKey="CLAIM_WARN"
                  values={{
                    currency: paymentType?.name,
                    chainType: paymentType?.networkName,
                  }}
                />
              }
              subText={t("CLAIM_WARN_SUBTEXT")}
            />
          )}

          <MainContainer $customMargin={12}>
            {claimInfos && (
              <>
                {claimInfos.length > 1 ? (
                  <Select
                    label={t("CHOOSE_CABINET")}
                    value={claimInfo?.cabinet || ""}
                    variants={cabinetOptions}
                    setValue={onSelectCabinet}
                  />
                ) : (
                  <AppText>
                    {t("CABINET")}:{" "}
                    <AppText isSpan fontWeight={600}>
                      {claimInfo?.cabinet || ""}
                    </AppText>
                  </AppText>
                )}
              </>
            )}

            <StyledTextInput
              value={withdrawAddress}
              setValue={(value) => setWithdrawAddress(value)}
              leftAdornment={<AppText>{t("ADDRESS")}</AppText>}
            />

            {hasMemo && (
              <StyledTextInput
                value={withdrawMemo}
                setValue={(value) => setWithdrawMemo(value)}
                leftAdornment={<AppText>Memo</AppText>}
              />
            )}

            <TransactionGroup>
              <CurrencyInput
                value={claimInfo?.amount || "0"}
                currency={claimInfo?.currencyName}
                currencies={currencies}
                placeholder="0"
                noSelect
                style={{ pointerEvents: "none" }}
              />
            </TransactionGroup>
          </MainContainer>

          <SemiContainer>
            <WithdrawInfoRows claimInfo={claimInfo} />

            <Checkbox
              checked={skipConfirm}
              onClick={() => setSkipConfirm(!skipConfirm)}
              label={t("WITHOUT_CONFIMATION")}
              variant="info"
            />

            <Checkbox
              checked={isWarnConfirmed}
              onClick={() => setIsWarnConfirmed(!isWarnConfirmed)}
              label={t("PROCEDURE_WARN")}
              variant="warn"
            />

            {!isMFAEnabled && (
              <Warning
                text={t("TWO_FA_FOR_CLAIM")}
                extraNode={
                  <MainButton
                    maxWidth="14rem"
                    color="secondary"
                    onClick={() => change2Fa()}
                  >
                    <LockIcon />
                    {t("ENABLE")}
                  </MainButton>
                }
              />
            )}

            <ModalButton
              disabled={
                (claimInfo && Number(claimInfo?.amount) <= 0) ||
                !isWarnConfirmed ||
                !chosenNetwork ||
                !isMFAEnabled ||
                isLoading ||
                withdrawAddress.length < 4 ||
                (hasMemo && withdrawMemo.length < 4)
              }
              type="submit"
            >
              {t("CLAIM")}{" "}
              {claimInfo &&
                Number(claimInfo.amount) > 0 &&
                toLocaleStringWithCurrency(
                  claimInfo && Number(claimInfo.amount),
                  claimInfo?.currencyName || "USDT",
                  currencies
                )}{" "}
              {claimInfo && Number(claimInfo.amount) > 0 && claimInfo?.feeAmount
                ? `- ${claimInfo.feeAmount} ${
                    claimInfo.feeCurrencyName || "USDT"
                  } ${t("FEES")}`
                : ""}
            </ModalButton>
          </SemiContainer>
        </InnerContentForm>
      </ModalContent>
    </Fade>
  );
};

export default observer(MainWithdrawStep);
